/**
 * Turtle popup dialog — "Which is right for me?" guide modal.
 *
 * Responsibilities:
 * - Open via `[data-turtle-popup-open="<section.id>"]` triggers (CB / BYO headers)
 * - Close via close button, backdrop click, or Escape
 * - Section 4 bundle cards call `complete-bundles.selectByProductId` then close
 *
 * Shell: custom `<turtle-dialog>` (not native <dialog>).
 *
 * Does not modify theme.js.liquid.
 */

/** Set true to enable console debug logs */
const DEBUG = false;

/**
 * Modal shell similar to native <dialog showModal()>, without relying on <dialog>.
 * Open state: [open] attribute. Clicking the host (backdrop) closes; panel clicks do not.
 */
if (!customElements.get('turtle-dialog')) {
  class TurtleDialog extends HTMLElement {
    connectedCallback() {
      this.setAttribute('role', 'dialog');
      this.setAttribute('aria-modal', 'true');
      this._onKeydown = this.onKeydown.bind(this);
      this._onHostClick = this.onHostClick.bind(this);
      this.addEventListener('click', this._onHostClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onHostClick);
      document.removeEventListener('keydown', this._onKeydown);
      this.unlockScroll();
    }

    get open() {
      return this.hasAttribute('open');
    }

    showModal() {
      if (this.open) {
        return;
      }

      this.setAttribute('open', '');
      this.lockScroll();
      document.addEventListener('keydown', this._onKeydown);
    }

    close() {
      if (!this.open) {
        return;
      }

      this.removeAttribute('open');
      this.unlockScroll();
      document.removeEventListener('keydown', this._onKeydown);
      this.dispatchEvent(new Event('close', { bubbles: true }));
    }

    onHostClick(event) {
      // Backdrop = click directly on <turtle-dialog>, not descendants
      if (event.target === this) {
        this.close();
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    }

    lockScroll() {
      document.documentElement.classList.add('turtle-dialog-open');
    }

    unlockScroll() {
      if (!document.querySelector('turtle-dialog[open]')) {
        document.documentElement.classList.remove('turtle-dialog-open');
      }
    }
  }

  customElements.define('turtle-dialog', TurtleDialog);
}

if (!customElements.get('turtle-popup-dialog')) {
  class TurtlePopupDialog extends HTMLElement {
    connectedCallback() {
      this._dialog = this.querySelector('turtle-dialog.turtle-popup__dialog');
      this._sectionId = this.dataset.sectionId || '';
      this._onDocumentClick = this.onDocumentClick.bind(this);
      this._onCloseClick = this.close.bind(this);

      if (!this._dialog) {
        return;
      }

      this.querySelectorAll('[data-turtle-popup-close]').forEach((button) => {
        button.addEventListener('click', this._onCloseClick);
      });

      document.addEventListener('click', this._onDocumentClick);

      this.querySelectorAll('.turtle-popup__bundle-card').forEach((card) => {
        card.addEventListener('click', () => this.onBundleCardClick(card));
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocumentClick);
    }

    log(label, ...args) {
      if (!DEBUG) return;
      console.log(`[Turtle Popup · ${label}]`, ...args);
    }

    get isOpen() {
      return Boolean(this._dialog?.open);
    }

    open() {
      if (!this._dialog || this._dialog.open) {
        return;
      }

      this._dialog.showModal();
      this.log('opened');
    }

    close() {
      if (!this._dialog || !this._dialog.open) {
        return;
      }

      this._dialog.close();
      this.log('closed');
    }

    /** Open when a matching guide-link trigger is clicked */
    onDocumentClick(event) {
      const trigger = event.target.closest('[data-turtle-popup-open]');

      if (!trigger) {
        return;
      }

      if (String(trigger.dataset.turtlePopupOpen) !== String(this._sectionId)) {
        return;
      }

      event.preventDefault();
      this.open();
    }

    /**
     * Select matching Complete Bundles card, then close.
     * @param {HTMLElement} card
     */
    onBundleCardClick(card) {
      const productId = card.dataset.productId;

      if (!productId) {
        return;
      }

      const completeBundles = this.ownerDocument.querySelector('complete-bundles');

      if (completeBundles && typeof completeBundles.selectByProductId === 'function') {
        completeBundles.selectByProductId(productId);
        this.log('bundle selected', { productId });
      } else {
        this.log('complete-bundles missing', { productId });
      }

      this.close();
    }
  }

  customElements.define('turtle-popup-dialog', TurtlePopupDialog);
}
